import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { UploadCloud, ImagePlus, Sparkles, Lock, Check, X, TrendingUp, Loader2, MapPin } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const readAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export const AIVenueAnalyzer = () => {
  const { t } = useLang();
  const v = t.venue;
  const planRef = useRef(null);
  const photoRef = useRef(null);
  const [plan, setPlan] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [sqm, setSqm] = useState("");
  const [budget, setBudget] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [localita, setLocalita] = useState("");

  const onPlan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPlan({ url: await readAsDataUrl(file), name: file.name });
  };
  const onPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - photos.length);
    const items = await Promise.all(files.map(async (f) => ({ url: await readAsDataUrl(f) })));
    setPhotos((p) => [...p, ...items].slice(0, 5));
  };

  const generate = async () => {
    if (!plan) { toast.error(v.errorPlan); return; }
    setLoading(true); setReport(null);
    try {
      const { data } = await axios.post(`${API}/venue/analyze`, {
        planimetria_base64: plan.url,
        photos_base64: photos.map((p) => p.url),
        superficie: sqm, budget, note,
      });
      setReport(data);
    } catch (err) { toast.error(v.errorGen); }
    finally { setLoading(false); }
  };
  const suggest = async () => {
    if (!localita.trim()) { toast.error(v.suggestNeedLocation); return; }
    setSuggesting(true);
    try { const { data } = await axios.post(`${API}/venue/suggest`, { localita: localita.trim() }); setSuggestions(data.macrozones || []); }
    catch (err) { toast.error(v.errorGen); }
    finally { setSuggesting(false); }
  };

  const econKeys = ["investimento", "ticketMedio", "scontriniGiorno", "roiMesi", "fatturatoAnno"];
  const field = "w-full rounded-xl border border-line bg-white px-4 py-3 text-base text-ink placeholder-muted focus:border-tricolor-green focus:ring-2 focus:ring-tricolor-green/20 focus:outline-none transition-[border-color,box-shadow] duration-200";

  return (
    <section id="analyzer" className="py-20 md:py-28 bg-surface border-y border-line" data-testid="analyzer-section">
      <div className="mx-auto max-w-[1280px] px-5 md:px-8">
        <div className="mb-10 md:mb-14 max-w-3xl">
          <h2 className="font-display font-extrabold tracking-tight text-3xl md:text-5xl text-ink">{v.sectionTitle}</h2>
          <p className="mt-4 text-base md:text-lg text-muted leading-relaxed">{v.sectionSub}</p>
        </div>

        <div className="rounded-[2rem] border border-line bg-white p-6 md:p-10">
          <span className="pill bg-tricolor-green/10 text-tricolor-green">{v.kicker}</span>
          <h3 className="mt-4 font-display font-extrabold text-2xl md:text-3xl text-ink">{v.title}</h3>
          <p className="mt-2 text-muted max-w-2xl">{v.subtitle}</p>

          <div className="mt-8 grid lg:grid-cols-2 gap-6">
            {/* Uploader */}
            <div data-testid="analyzer-uploader">
              <p className="text-sm font-semibold text-ink mb-3">{v.planLabel}</p>
              <input ref={planRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={onPlan} data-testid="plan-input" />
              <button onClick={() => planRef.current?.click()} className="w-full rounded-2xl border-2 border-dashed border-line hover:border-tricolor-green transition-colors duration-200 p-8 flex flex-col items-center gap-3 text-muted" data-testid="plan-upload-btn">
                {plan ? <img src={plan.url} alt="planimetria" className="max-h-40 rounded-lg object-contain" /> : (<>
                  <UploadCloud size={32} className="text-tricolor-green" />
                  <span className="font-semibold text-ink">{v.planCta}</span>
                  <span className="text-xs">{v.planHint}</span>
                </>)}
              </button>
              {plan && <p className="mt-2 text-xs text-tricolor-green flex items-center gap-1"><Check size={13} /> {plan.name}</p>}

              <p className="text-sm font-semibold text-ink mt-6 mb-3">{v.photosLabel}</p>
              <input ref={photoRef} type="file" accept="image/png,image/jpeg" multiple className="hidden" onChange={onPhotos} data-testid="photos-input" />
              <div className="flex flex-wrap gap-3">
                {photos.map((p, i) => (
                  <div key={i} className="relative w-16 h-16 rounded-xl overflow-hidden border border-line">
                    <img src={p.url} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setPhotos((ps) => ps.filter((_, idx) => idx !== i))} className="absolute top-0 right-0 bg-ink/80 text-white p-0.5"><X size={12} /></button>
                  </div>
                ))}
                {photos.length < 5 && (
                  <button onClick={() => photoRef.current?.click()} className="w-16 h-16 rounded-xl border-2 border-dashed border-line hover:border-tricolor-green transition-colors duration-200 flex items-center justify-center text-muted" data-testid="photos-add-btn"><ImagePlus size={20} /></button>
                )}
              </div>

              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                <input value={sqm} onChange={(e) => setSqm(e.target.value)} placeholder={v.sqmLabel} className={field} data-testid="sqm-input" />
                <input value={budget} onChange={(e) => setBudget(e.target.value)} placeholder={v.budgetLabel} className={field} data-testid="budget-input" />
              </div>
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={v.notePlaceholder} className={`${field} mt-4 resize-none`} data-testid="note-input" />

              <button onClick={generate} disabled={loading} className="btn-primary w-full mt-6 disabled:opacity-60" data-testid="analyze-btn">
                {loading ? <><Loader2 size={18} className="animate-spin" /> {v.generating}</> : <><Sparkles size={18} /> {v.generate}</>}
              </button>
              <p className="mt-4 text-xs text-muted flex items-center gap-2"><Lock size={13} /> {v.privacy}</p>
            </div>

            {/* Result */}
            <div className="rounded-2xl border border-line bg-surface p-6 min-h-[300px]" data-testid="analyzer-result">
              <AnimatePresence mode="wait">
                {report ? (
                  <motion.div key="report" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                    <div className="flex items-center justify-between">
                      <span className="eyebrow">{v.resultTitle}</span>
                      <div className="text-right">
                        <div className="font-display font-extrabold text-4xl text-tricolor-green leading-none">{report.score}<span className="text-xl text-muted">/100</span></div>
                        <div className="text-[11px] text-muted uppercase tracking-wide">{v.scoreLabel}</div>
                      </div>
                    </div>
                    {report.verdict && <p className="mt-4 text-ink/80">{report.verdict}</p>}
                    {report.format && <p className="mt-3 text-sm text-muted"><span className="text-ink/40">{v.formatLabel}: </span>{report.format}{report.sqm ? ` · ${report.sqm}` : ""}</p>}
                    {report.economics && (
                      <div className="mt-5">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted mb-2">{v.econLabel}</p>
                        <div className="grid grid-cols-2 gap-px bg-line rounded-xl overflow-hidden">
                          {econKeys.filter((k) => report.economics[k]).map((k) => (
                            <div key={k} className="bg-white p-3"><div className="text-sm font-semibold text-ink">{report.economics[k]}</div><div className="text-[10px] text-muted uppercase tracking-wide">{v.econKeys[k]}</div></div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="mt-5 grid sm:grid-cols-2 gap-4">
                      {report.pros?.length > 0 && (<div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600 mb-2">{v.prosLabel}</p>
                        <ul className="space-y-1.5">{report.pros.map((p, i) => <li key={i} className="text-sm text-ink/70 flex gap-2"><Check size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />{p}</li>)}</ul>
                      </div>)}
                      {report.cons?.length > 0 && (<div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-tricolor-red mb-2">{v.consLabel}</p>
                        <ul className="space-y-1.5">{report.cons.map((cc, i) => <li key={i} className="text-sm text-ink/70 flex gap-2"><X size={14} className="text-tricolor-red mt-0.5 flex-shrink-0" />{cc}</li>)}</ul>
                      </div>)}
                    </div>
                    {report.layout && <div className="mt-5"><p className="text-xs font-semibold uppercase tracking-wide text-muted mb-1">{v.layoutLabel}</p><p className="text-sm text-ink/70 leading-relaxed">{report.layout}</p></div>}
                    {report.recommendations?.length > 0 && (<div className="mt-5"><p className="text-xs font-semibold uppercase tracking-wide text-muted mb-2">{v.recoLabel}</p>
                      <ul className="space-y-1.5">{report.recommendations.map((r, i) => <li key={i} className="text-sm text-ink/70 flex gap-2"><Sparkles size={14} className="text-tricolor-green mt-0.5 flex-shrink-0" />{r}</li>)}</ul></div>)}
                  </motion.div>
                ) : (
                  <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-col items-center justify-center text-center py-10">
                    <Sparkles size={40} className="text-line" />
                    <p className="mt-4 text-muted max-w-xs">{v.subtitle}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Suggest */}
        <div className="mt-6 rounded-[2rem] border border-line bg-white p-6 md:p-8" data-testid="analyzer-suggest">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <span className="w-12 h-12 rounded-2xl bg-tricolor-green/10 text-tricolor-green flex items-center justify-center flex-shrink-0"><MapPin size={22} /></span>
              <div>
                <h3 className="font-display font-bold text-xl text-ink">{v.suggestTitle}</h3>
                <p className="mt-1 text-sm text-muted max-w-xl">{v.suggestBody}</p>
              </div>
            </div>
          </div>
          <div className="mt-5 flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={localita}
              onChange={(e) => setLocalita(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") suggest(); }}
              placeholder={v.suggestPlaceholder}
              className="flex-1 rounded-xl border border-line bg-white px-4 py-3 focus:outline-none focus:border-tricolor-green"
              data-testid="suggest-location-input"
            />
            <button onClick={suggest} disabled={suggesting} className="btn-primary flex-shrink-0 disabled:opacity-60" data-testid="suggest-btn">
              {suggesting ? <><Loader2 size={18} className="animate-spin" /> {v.suggesting}</> : <><TrendingUp size={18} /> {v.suggestCta}</>}
            </button>
          </div>
          {suggestions && (
            <div className="mt-6 space-y-6">
              {suggestions.map((mz, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="rounded-2xl border border-line bg-surface overflow-hidden" data-testid={`macrozone-${i}`}>
                  <div className="p-5 md:p-6 border-b border-line bg-white">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-tricolor-green">{`Macrozona ${i + 1}`}</span>
                        <h4 className="font-display font-extrabold text-xl text-ink">{mz.name}</h4>
                      </div>
                      <span className="flex-shrink-0 rounded-full bg-tricolor-green text-white font-display font-bold px-3 py-1 text-sm">{mz.score}/100</span>
                    </div>
                    {mz.strengths && <p className="mt-2 text-sm text-muted leading-relaxed">{mz.strengths}</p>}
                  </div>

                  <div className="p-4 md:p-5">
                    <div className="text-xs font-semibold uppercase tracking-wide text-ink mb-3">{v.macroStreetsLabel}</div>
                    <div className="grid md:grid-cols-3 gap-4">
                      {(mz.streets || []).map((st, j) => (
                        <div key={j} className="rounded-xl border border-line bg-white overflow-hidden flex flex-col" data-testid={`macrozone-${i}-street-${j}`}>
                          <div className="relative h-32">
                            <iframe
                              title={`map-${i}-${j}`}
                              src={`https://maps.google.com/maps?q=${encodeURIComponent(st.mapsQuery || st.name)}&z=14&output=embed`}
                              className="absolute inset-0 w-full h-full"
                              style={{ border: 0 }}
                              loading="lazy"
                              referrerPolicy="no-referrer-when-downgrade"
                              data-testid={`macrozone-${i}-street-${j}-map`}
                            />
                          </div>
                          <div className="p-3.5 flex flex-col gap-3 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <span className="text-sm font-bold text-ink flex items-center gap-1.5"><MapPin size={13} className="text-tricolor-green flex-shrink-0" /> {st.name}</span>
                              <span className="flex-shrink-0 rounded-full bg-tricolor-green/10 text-tricolor-green font-bold px-2 py-0.5 text-xs">{st.score}</span>
                            </div>

                            <div className="space-y-1.5 text-xs">
                              {st.passaggio && <div><span className="font-semibold text-ink">{v.passaggioLabel}: </span><span className="text-muted">{st.passaggio}</span></div>}
                              {st.parcheggio && <div><span className="font-semibold text-ink">{v.parcheggioLabel}: </span><span className="text-muted">{st.parcheggio}</span></div>}
                              {st.categoriaUtenti && <div><span className="font-semibold text-ink">{v.utentiLabel}: </span><span className="text-muted">{st.categoriaUtenti}</span></div>}
                            </div>

                            <div className="mt-auto grid grid-cols-3 gap-2 pt-2 border-t border-line text-center">
                              <div>
                                <div className="text-[9px] uppercase tracking-wide text-muted">{v.ticketAvgLabel}</div>
                                <div className="text-sm font-bold text-ink">{st.ticketMedio || "—"}</div>
                              </div>
                              <div>
                                <div className="text-[9px] uppercase tracking-wide text-muted">{v.receiptsDayLabel}</div>
                                <div className="text-sm font-bold text-ink">{st.scontriniGiorno ?? "—"}</div>
                              </div>
                              <div>
                                <div className="text-[9px] uppercase tracking-wide text-muted">{v.suggestRentLabel}</div>
                                <div className="text-[11px] font-bold text-tricolor-green leading-tight">{st.rent || "—"}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
